import { ApiError } from "@/lib/apiClient";
import {
  Charge,
  CreateChargeDto,
  UpdateChargeDto,
  FetchChargesOptions,
  ChargeFilters,
} from "@/types/models/charge";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetStoreSpecificState } from "@/store/actions/storeActions";

export interface ChargesState {
  // Charge list state
  items: Charge[];
  loaded: boolean;
  loading: boolean;
  error: ApiError | null;
  pagination: {
    page: number;
    total: number;
    pageSize: number;
    totalPages: number;
  };

  // Single charge state
  currentCharge: Charge | null;
  chargeLoading: boolean;
  chargeError: ApiError | null;

  // Search state
  searchResults: Charge[];
  searchLoading: boolean;
  searchError: ApiError | null;
  searchPagination: {
    page: number;
    total: number;
    pageSize: number;
    totalPages: number;
  };

  // Form state
  formState: {
    loading: boolean;
    error: ApiError | null;
    success: boolean;
  };

  // Filters
  filters: ChargeFilters;
  fetchOptions: FetchChargesOptions;
}

const initialState: ChargesState = {
  items: [],
  loaded: false,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },

  currentCharge: null,
  chargeLoading: false,
  chargeError: null,

  searchResults: [],
  searchLoading: false,
  searchError: null,
  searchPagination: {
    page: 1,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },

  formState: {
    loading: false,
    error: null,
    success: false,
  },

  filters: {},
  fetchOptions: {
    page: 1,
    page_size: 10,
    sort_by: "created_at",
    sort_order: "desc",
  },
};

const chargeSlice = createSlice({
  name: "charges",
  initialState,
  reducers: {
    // Fetch charges
    fetchChargesRequest: (
      state,
      action: PayloadAction<FetchChargesOptions>,
    ) => {
      state.loading = true;
      state.error = null;
      state.fetchOptions = { ...state.fetchOptions, ...action.payload };
    },
    fetchChargesSuccess: (
      state,
      action: PayloadAction<{ data: Charge[]; pagination: any }>,
    ) => {
      state.loading = false;
      state.loaded = true;
      state.items = action.payload.data;
      state.pagination = action.payload.pagination;
    },
    fetchChargesFailure: (state, action: PayloadAction<ApiError>) => {
      state.loading = false;
      state.loaded = true;
      state.error = action.payload;
    },

    // Search charges
    searchChargesRequest: (
      state,
      _action: PayloadAction<{ query: string; options?: FetchChargesOptions }>,
    ) => {
      state.searchLoading = true;
      state.searchError = null;
    },
    searchChargesSuccess: (
      state,
      action: PayloadAction<{ data: Charge[]; pagination: any }>,
    ) => {
      state.searchLoading = false;
      state.searchResults = action.payload.data;
      state.searchPagination = action.payload.pagination;
    },
    searchChargesFailure: (state, action: PayloadAction<ApiError>) => {
      state.searchLoading = false;
      state.searchError = action.payload;
    },

    // Get single charge
    fetchChargeDetailsRequest: (
      state,
      _action: PayloadAction<{ id: string }>,
    ) => {
      state.chargeLoading = true;
      state.chargeError = null;
    },
    fetchChargeDetailsSuccess: (state, action: PayloadAction<Charge>) => {
      state.chargeLoading = false;
      state.currentCharge = action.payload;
    },
    fetchChargeDetailsFailure: (state, action: PayloadAction<ApiError>) => {
      state.chargeLoading = false;
      state.chargeError = action.payload;
    },

    // Create charge
    createChargeRequest: (state, _action: PayloadAction<CreateChargeDto>) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    createChargeSuccess: (state, action: PayloadAction<Charge>) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.items = [action.payload, ...state.items];
    },
    createChargeFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    // Update charge
    updateChargeRequest: (
      state,
      _action: PayloadAction<{ id: string; data: UpdateChargeDto }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    updateChargeSuccess: (state, action: PayloadAction<Charge>) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.currentCharge = action.payload;

      const index = state.items.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    updateChargeFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    // Delete charge
    deleteChargeRequest: (state, _action: PayloadAction<{ id: string }>) => {
      state.formState.loading = true;
      state.formState.error = null;
    },
    deleteChargeSuccess: (state, action: PayloadAction<{ id: string }>) => {
      state.formState.loading = false;
      state.items = state.items.filter((item) => item.id !== action.payload.id);
    },
    deleteChargeFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    // Utility actions
    setFilters: (state, action: PayloadAction<ChargeFilters>) => {
      state.filters = action.payload;
    },
    resetFormState: (state) => {
      state.formState = {
        loading: false,
        error: null,
        success: false,
      };
    },
    clearCurrentCharge: (state) => {
      state.currentCharge = null;
      state.chargeError = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreSpecificState, () => initialState);
  },
});

export const {
  fetchChargesRequest,
  fetchChargesSuccess,
  fetchChargesFailure,
  searchChargesRequest,
  searchChargesSuccess,
  searchChargesFailure,
  fetchChargeDetailsRequest,
  fetchChargeDetailsSuccess,
  fetchChargeDetailsFailure,
  createChargeRequest,
  createChargeSuccess,
  createChargeFailure,
  updateChargeRequest,
  updateChargeSuccess,
  updateChargeFailure,
  deleteChargeRequest,
  deleteChargeSuccess,
  deleteChargeFailure,
  setFilters,
  resetFormState,
  clearCurrentCharge,
} = chargeSlice.actions;

export default chargeSlice.reducer;
